import type {
  AdminCoverageDashboard,
  AdminCoverageMetrics,
  AdminCoverageSection,
  CoverageAnalyticsScope
} from '../domain/types';
import { addReadableStreetBasemap } from '../map/carto-basemap';
import { makeMapPageFriendly } from '../map/page-friendly-map';
import { mapZoom, mapZoomBand, zoomInterpolate } from '../map/zoom-presentation';

declare const L: any;

const FALLBACK_CENTER: [number, number] = [33.437, -112.476];
const COLORS = {
  UNASSIGNED: '#7f93a3',
  ASSIGNED: '#1673b8',
  COMPLETE: '#2f8a57',
  SELECTED: '#175cd3',
  EMPTY_LOT: '#d18a00',
  UNDER_CONSTRUCTION: '#8057b7',
  NO_SOLICITING: '#c2413b'
} as const;

export interface AdminCoverageMapOptions {
  initialScope?: CoverageAnalyticsScope;
  initialView?: { center: [number, number]; zoom: number };
  canReturnSection?: (section: AdminCoverageSection) => boolean;
  onReturnSection?: (section: AdminCoverageSection) => Promise<void> | void;
}

export interface AdminCoverageMapHandle {
  setSearch(query: string): void;
  setScope(scope: CoverageAnalyticsScope): AdminCoverageMetrics;
  focusSection(sectionId: string): void;
  destroy(): void;
}

export interface SectionPickerMapOptions {
  showUnavailable?: boolean;
  initialSelectedIds?: string[];
  collapseUnavailableStatuses?: boolean;
}

export interface SectionPickerMapHandle {
  clear(): void;
  getSelectedIds(): string[];
  setSearch(query: string): void;
  destroy(): void;
}

export function mountAdminCoverageMap(
  elementId: string,
  dashboard: AdminCoverageDashboard,
  options: AdminCoverageMapOptions = {}
): AdminCoverageMapHandle | null {
  const element = document.querySelector<HTMLDivElement>(`#${CSS.escape(elementId)}`);
  if (!element || typeof L === 'undefined') return null;

  const map = createMap(element);
  const bounds = L.latLngBounds([]);
  const layerById = new Map<string, any>();
  const sectionById = new Map(dashboard.sections.map((section) => [section.id, section]));
  const neighborhoodSummaryLayer = L.layerGroup();
  const conditionMarkerLayer = L.layerGroup();
  let currentScope: CoverageAnalyticsScope = options.initialScope ?? { type: 'OVERALL', id: 'OVERALL', label: 'Whole church' };
  let currentSearch = '';

  for (const section of dashboard.sections) {
    if (!section.geometry) continue;
    const feature = {
      type: 'Feature',
      properties: { sectionId: section.id },
      geometry: section.geometry
    };
    const layer = L.geoJSON(feature, {
      style: sectionStyle(section, true, currentScope, 15),
      onEachFeature(_feature: unknown, sectionLayer: any) {
        sectionLayer.bindTooltip(sectionTooltip(section), {
          sticky: true,
          direction: 'top',
          className: 'coverage-section-tooltip'
        });
        sectionLayer.bindPopup(sectionPopup(section, Boolean(options.canReturnSection?.(section))), { maxWidth: 360 });
        if (options.onReturnSection && options.canReturnSection?.(section)) {
          sectionLayer.on('popupopen', (event: any) => {
            const popupElement = event.popup?.getElement?.() as HTMLElement | undefined;
            const button = popupElement?.querySelector<HTMLButtonElement>('[data-return-section]');
            if (!button || button.dataset.wired === 'true') return;
            button.dataset.wired = 'true';
            button.addEventListener('click', () => {
              button.disabled = true;
              button.textContent = 'Taking back…';
              Promise.resolve(options.onReturnSection?.(section)).catch((error) => {
                button.disabled = false;
                button.textContent = 'Take back section';
                window.alert(error instanceof Error ? error.message : 'Could not take back this section.');
              });
            });
          });
        }
      }
    }).addTo(map);
    layerById.set(section.id, layer);
    const layerBounds = layer.getBounds?.();
    if (layerBounds?.isValid?.()) bounds.extend(layerBounds);
  }

  for (const section of dashboard.sections) {
    for (const point of section.conditionPoints ?? []) {
      const color = conditionColor(point.conditionStatus);
      const marker = L.circleMarker([point.lat, point.lng], {
        radius: 6,
        color: '#ffffff',
        weight: 2,
        fillColor: color,
        fillOpacity: 1,
        opacity: 1,
        bubblingMouseEvents: false
      });
      const label = conditionLabel(point.conditionStatus);
      const address = point.address || 'Address unavailable';
      marker.bindTooltip(`<strong>${escapeHtml(address)}</strong><br>${escapeHtml(label)}`, {
        sticky: true,
        direction: 'top',
        className: 'coverage-condition-tooltip'
      });
      marker.bindPopup(`<div class="coverage-popup"><div class="coverage-popup-title">${escapeHtml(address)}</div><div><strong>Property marker:</strong> ${escapeHtml(label)}</div><div><strong>Section:</strong> ${escapeHtml(section.neighborhoodName)} ${section.sectionNumber}</div><div><strong>Doors in section:</strong> ${section.doors.toLocaleString()}</div></div>`, { maxWidth: 300 });
      marker.addTo(conditionMarkerLayer);
    }
  }
  if (conditionMarkerLayer.getLayers().length) conditionMarkerLayer.addTo(map);

  // Leaflet must have an initial center/zoom before neighborhood labels are projected
  // with latLngToLayerPoint(). Fit the map first, then render the summary labels.
  const storedView = options.initialView ?? readSavedMapView(elementId);
  if (storedView) map.setView(storedView.center, storedView.zoom, { animate: false });
  else fitMap(map, bounds, dashboard.scope === 'MASTER' ? 11 : 14);
  populateNeighborhoodSummaryLayer(
    map,
    neighborhoodSummaryLayer,
    dashboard.sections,
    layerById,
    'ALL'
  );

  const refreshStyles = (): void => {
    const normalized = normalize(currentSearch);
    const zoom = mapZoom(map);
    const far = mapZoomBand(zoom) === 'FAR';
    for (const section of dashboard.sections) {
      const layer = layerById.get(section.id);
      if (!layer) continue;
      const match = !normalized || sectionSearchText(section).includes(normalized);
      layer.setStyle?.(sectionStyle(section, match, currentScope, zoom));
      setLayerInteractive(layer, !far);
    }
    setSummaryLayerVisible(map, neighborhoodSummaryLayer, true);
  };

  const handleZoomEnd = (): void => refreshStyles();
  const handleViewChange = (): void => saveMapView(elementId, map);
  map.on('zoomend', handleZoomEnd);
  map.on('moveend', handleViewChange);
  refreshStyles();

  const setSearch = (query: string): void => {
    currentSearch = query;
    const normalized = normalize(query);
    const matches: any[] = [];
    for (const section of dashboard.sections) {
      const layer = layerById.get(section.id);
      if (!layer) continue;
      if (normalized && sectionSearchText(section).includes(normalized)) matches.push(layer);
    }
    if (normalized && matches.length) {
      const searchBounds = L.latLngBounds([]);
      for (const layer of matches) {
        const layerBounds = layer.getBounds?.();
        if (layerBounds?.isValid?.()) searchBounds.extend(layerBounds);
      }
      if (searchBounds.isValid()) map.fitBounds(searchBounds.pad(0.16), { maxZoom: 17 });
    } else if (!normalized && bounds.isValid()) {
      map.fitBounds(bounds.pad(0.08), { maxZoom: 16 });
    }
    refreshStyles();
  };

  return {
    setSearch,
    setScope(scope: CoverageAnalyticsScope): AdminCoverageMetrics {
      currentScope = scope;
      refreshStyles();
      return coverageMetricsForScope(dashboard.sections, scope);
    },
    focusSection(sectionId: string): void {
      const layer = layerById.get(sectionId);
      const section = sectionById.get(sectionId);
      if (!layer || !section) return;
      const layerBounds = layer.getBounds?.();
      if (layerBounds?.isValid?.()) map.fitBounds(layerBounds.pad(0.35), { maxZoom: 18 });
      const child = layer.getLayers?.()[0];
      child?.openPopup?.();
    },
    destroy(): void {
      map.off('zoomend', handleZoomEnd);
      map.off('moveend', handleViewChange);
      map.remove();
    }
  };
}

export function mountSectionPickerMap(
  elementId: string,
  dashboard: AdminCoverageDashboard,
  onSelectionChange: (sections: AdminCoverageSection[]) => void,
  maximumSelections = 100,
  options: SectionPickerMapOptions = {}
): SectionPickerMapHandle | null {
  const element = document.querySelector<HTMLDivElement>(`#${CSS.escape(elementId)}`);
  if (!element || typeof L === 'undefined') return null;

  const allSections = dashboard.sections.filter((section) => section.geometry);
  const available = allSections.filter((section) => section.status === 'UNASSIGNED');
  const displayed = options.showUnavailable ? allSections : available;
  if (!displayed.length) {
    element.innerHTML = '<div class="map-empty-state"><strong>No published sections</strong><p>There are no sections available to display.</p></div>';
    return null;
  }

  const map = createMap(element);
  const bounds = L.latLngBounds([]);
  const selected = new Set(
    (options.initialSelectedIds ?? []).filter((id) => available.some((section) => section.id === id))
  );
  const visibleLayerById = new Map<string, any>();
  const hitLayerById = new Map<string, any>();
  const neighborhoodSummaryLayer = L.layerGroup();
  let currentSearch = '';

  const emit = (): void => {
    onSelectionChange(available.filter((section) => selected.has(section.id)));
  };
  const matchesSearch = (section: AdminCoverageSection): boolean => {
    const query = normalize(currentSearch);
    return !query || sectionSearchText(section).includes(query);
  };
  const restyle = (section: AdminCoverageSection): void => {
    const layer = visibleLayerById.get(section.id);
    if (!layer) return;
    const isSelected = selected.has(section.id);
    const match = matchesSearch(section);
    const zoom = mapZoom(map);
    const far = mapZoomBand(zoom) === 'FAR';
    layer.setStyle?.(pickerSectionStyle(section, isSelected, match, zoom, options.collapseUnavailableStatuses === true));
    setLayerInteractive(layer, !far && section.status === 'UNASSIGNED');

    const hit = hitLayerById.get(section.id);
    if (hit) {
      hit.setStyle?.(pickerHitStyle(zoom, match));
      setLayerInteractive(hit, !far && match);
    }
  };
  const refreshZoomPresentation = (): void => {
    for (const section of displayed) restyle(section);
    setSummaryLayerVisible(map, neighborhoodSummaryLayer, true);
  };
  const toggle = (section: AdminCoverageSection): void => {
    if (section.status !== 'UNASSIGNED') return;
    if (selected.has(section.id)) selected.delete(section.id);
    else {
      if (selected.size >= maximumSelections) {
        window.alert(`Select no more than ${maximumSelections} sections at once.`);
        return;
      }
      selected.add(section.id);
    }
    restyle(section);
    emit();
  };

  for (const section of displayed) {
    const feature = { type: 'Feature', properties: { sectionId: section.id }, geometry: section.geometry };
    const visible = L.geoJSON(feature, {
      style: pickerSectionStyle(
        section,
        selected.has(section.id),
        true,
        15,
        options.collapseUnavailableStatuses === true
      )
    }).addTo(map);
    const status = section.status === 'UNASSIGNED'
      ? 'Not assigned'
      : section.status === 'COMPLETE'
        ? 'Completed'
        : section.reservedForGroup && !section.assignmentId
          ? 'Reserved for group'
          : 'Assigned';
    const action = section.status === 'UNASSIGNED'
      ? `<br><em>Click to ${selected.has(section.id) ? 'remove' : 'select'}</em>`
      : '';
    const tooltip = [
      `<strong>${escapeHtml(section.neighborhoodName)} · Section ${section.sectionNumber}</strong>`,
      `${escapeHtml(status)} · ${section.doors.toLocaleString()} doors`,
      section.assignedTo ? `Assigned to ${escapeHtml(section.assignedTo.displayName)}` : '',
      section.groupName ? `Group: ${escapeHtml(section.groupName)}` : '',
      section.streetNames.length ? escapeHtml(section.streetNames.join(', ')) : ''
    ].filter(Boolean).join('<br>') + action;
    visible.bindTooltip(tooltip, { sticky: true, direction: 'top', className: 'coverage-section-tooltip' });

    if (section.status === 'UNASSIGNED') {
      const hit = L.geoJSON(feature, {
        style: pickerHitStyle(15, true)
      }).addTo(map);
      hit.bindTooltip(tooltip, { sticky: true, direction: 'top', className: 'coverage-section-tooltip' });
      visible.on('click', () => toggle(section));
      hit.on('click', () => toggle(section));
      hitLayerById.set(section.id, hit);
    }

    visibleLayerById.set(section.id, visible);
    const layerBounds = visible.getBounds?.();
    if (layerBounds?.isValid?.()) bounds.extend(layerBounds);
  }

  // Leaflet must have an initial center/zoom before neighborhood labels are projected
  // with latLngToLayerPoint(). Fit the map first, then render the summary labels.
  const storedView = readSavedMapView(elementId);
  if (storedView) map.setView(storedView.center, storedView.zoom, { animate: false });
  else fitMap(map, bounds, dashboard.scope === 'MASTER' ? 11 : 15);
  populateNeighborhoodSummaryLayer(
    map,
    neighborhoodSummaryLayer,
    displayed,
    visibleLayerById,
    options.showUnavailable ? 'ALL' : 'AVAILABLE'
  );
  const handleZoomEnd = (): void => refreshZoomPresentation();
  const handleViewChange = (): void => saveMapView(elementId, map);
  map.on('zoomend', handleZoomEnd);
  map.on('moveend', handleViewChange);
  refreshZoomPresentation();
  emit();

  return {
    clear(): void {
      selected.clear();
      refreshZoomPresentation();
      emit();
    },
    getSelectedIds(): string[] {
      return available.filter((section) => selected.has(section.id)).map((section) => section.id);
    },
    setSearch(query: string): void {
      currentSearch = query;
      const normalized = normalize(query);
      const matches: any[] = [];
      for (const section of displayed) {
        restyle(section);
        if (normalized && sectionSearchText(section).includes(normalized)) {
          const layer = visibleLayerById.get(section.id);
          if (layer) matches.push(layer);
        }
      }
      if (normalized && matches.length) {
        const searchBounds = L.latLngBounds([]);
        for (const layer of matches) {
          const layerBounds = layer.getBounds?.();
          if (layerBounds?.isValid?.()) searchBounds.extend(layerBounds);
        }
        if (searchBounds.isValid()) map.fitBounds(searchBounds.pad(0.16), { maxZoom: 17 });
      } else if (!normalized && bounds.isValid()) {
        map.fitBounds(bounds.pad(0.08), { maxZoom: 16 });
      }
      refreshZoomPresentation();
    },
    destroy(): void {
      map.off('zoomend', handleZoomEnd);
      map.off('moveend', handleViewChange);
      map.remove();
    }
  };
}

export function coverageMetricsForScope(
  sections: AdminCoverageSection[],
  scope: CoverageAnalyticsScope
): AdminCoverageMetrics {
  if (scope.type === 'OVERALL') return overallCoverageMetrics(sections);

  // A person or small group does not own a pool of otherwise-unassigned territory.
  // Their scoped analytics are calculated only from published sections connected to them.
  const scopedSections = sections.filter((section) =>
    section.status !== 'UNASSIGNED' && sectionMatchesScope(section, scope)
  );
  const metrics: AdminCoverageMetrics = {
    totalSections: scopedSections.length,
    totalDoors: 0,
    unassignedSections: 0,
    unassignedDoors: 0,
    assignedSections: 0,
    assignedDoors: 0,
    completedSections: 0,
    completedDoors: 0,
    completionPercent: 0
  };

  for (const section of scopedSections) {
    metrics.totalDoors += section.doors;
    if (section.status === 'COMPLETE') {
      metrics.completedSections += 1;
      metrics.completedDoors += section.doors;
    } else if (section.status === 'ASSIGNED') {
      metrics.assignedSections += 1;
      metrics.assignedDoors += section.doors;
    }
  }

  metrics.completionPercent = metrics.totalDoors
    ? Math.round(metrics.completedDoors / metrics.totalDoors * 1000) / 10
    : 0;
  return metrics;
}

function overallCoverageMetrics(sections: AdminCoverageSection[]): AdminCoverageMetrics {
  const metrics: AdminCoverageMetrics = {
    totalSections: sections.length,
    totalDoors: 0,
    unassignedSections: 0,
    unassignedDoors: 0,
    assignedSections: 0,
    assignedDoors: 0,
    completedSections: 0,
    completedDoors: 0,
    completionPercent: 0
  };
  for (const section of sections) {
    metrics.totalDoors += section.doors;
    if (section.status === 'COMPLETE') {
      metrics.completedSections += 1;
      metrics.completedDoors += section.doors;
    } else if (section.status === 'ASSIGNED') {
      metrics.assignedSections += 1;
      metrics.assignedDoors += section.doors;
    } else {
      metrics.unassignedSections += 1;
      metrics.unassignedDoors += section.doors;
    }
  }
  metrics.completionPercent = metrics.totalDoors
    ? Math.round(metrics.completedDoors / metrics.totalDoors * 1000) / 10
    : 0;
  return metrics;
}

export function sectionSearchText(section: AdminCoverageSection): string {
  return normalize([
    section.id,
    `section ${section.sectionNumber}`,
    String(section.sectionNumber),
    section.neighborhoodName,
    ...section.streetNames,
    section.status,
    section.assignedTo?.displayName ?? '',
    section.assignedBy?.displayName ?? '',
    section.groupName ?? '',
    ...section.teamMembers.map((member) => member.name)
  ].join(' '));
}

function createMap(element: HTMLElement): any {
  // Give Leaflet a real initial view at construction time. On iPhone Safari,
  // creating a map without center/zoom and projecting labels immediately after
  // fitBounds can still leave the map in Leaflet's unloaded state long enough
  // for latLngToLayerPoint() to throw "Set map center and zoom first."
  // The data-specific fitMap() call below will replace this fallback view.
  const map = L.map(element, {
    center: FALLBACK_CENTER,
    zoom: 11,
    zoomControl: true,
    attributionControl: true,
    scrollWheelZoom: false
  });
  makeMapPageFriendly(map, element);
  addReadableStreetBasemap(map, L);
  return map;
}

function fitMap(map: any, bounds: any, fallbackZoom: number): void {
  if (bounds.isValid()) map.fitBounds(bounds.pad(0.08), { maxZoom: 16 });
  else map.setView(FALLBACK_CENTER, fallbackZoom);
  window.requestAnimationFrame(() => map.invalidateSize());
}

function savedMapViewKey(elementId: string): string {
  return `buckeyeCoverageMapView:${elementId}`;
}

function readSavedMapView(elementId: string): { center: [number, number]; zoom: number } | undefined {
  try {
    const value = window.sessionStorage.getItem(savedMapViewKey(elementId));
    if (!value) return undefined;
    const parsed = JSON.parse(value) as { center?: [number, number]; zoom?: number };
    if (!Array.isArray(parsed.center) || parsed.center.length !== 2 || !parsed.center.every(Number.isFinite) || !Number.isFinite(parsed.zoom)) return undefined;
    return { center: parsed.center, zoom: parsed.zoom! };
  } catch {
    return undefined;
  }
}

function saveMapView(elementId: string, map: any): void {
  try {
    const center = map.getCenter?.();
    const zoom = map.getZoom?.();
    if (center && Number.isFinite(center.lat) && Number.isFinite(center.lng) && Number.isFinite(zoom)) {
      window.sessionStorage.setItem(savedMapViewKey(elementId), JSON.stringify({ center: [center.lat, center.lng], zoom }));
    }
  } catch {
    // Map navigation still works when browser storage is unavailable.
  }
}

function scopedState(section: AdminCoverageSection, scope: CoverageAnalyticsScope): AdminCoverageSection['status'] {
  if (scope.type === 'OVERALL') return section.status;
  if (!sectionMatchesScope(section, scope)) return 'UNASSIGNED';
  return section.status === 'COMPLETE' ? 'COMPLETE' : 'ASSIGNED';
}

function sectionMatchesScope(section: AdminCoverageSection, scope: CoverageAnalyticsScope): boolean {
  if (scope.type === 'OVERALL') return true;
  if (scope.type === 'GROUP') return section.groupId === scope.id;
  const identityIds = new Set([scope.id, ...(scope.aliases ?? [])].filter(Boolean));
  if (
    (section.assignedTo?.id && identityIds.has(section.assignedTo.id)) ||
    (section.assignedTo?.pcoPersonId && identityIds.has(section.assignedTo.pcoPersonId))
  ) return true;
  const normalized = normalize(scope.normalizedName ?? scope.label);
  if (normalize(section.assignedTo?.displayName ?? '') === normalized) return true;
  return section.teamMembers.some((member) =>
    (member.personId ? identityIds.has(member.personId) : false) || normalize(member.name) === normalized
  );
}

function sectionStyle(
  section: AdminCoverageSection,
  match: boolean,
  scope: CoverageAnalyticsScope,
  zoom: number
): Record<string, unknown> {
  const band = mapZoomBand(zoom);
  const weight = match
    ? zoomInterpolate(zoom, 2.2, 7.5)
    : zoomInterpolate(zoom, 1, 4.25);
  return {
    color: COLORS[scopedState(section, scope)],
    weight,
    opacity: band === 'FAR' ? 0 : band === 'MEDIUM' ? (match ? 0.58 : 0.04) : (match ? 0.92 : 0.08),
    lineCap: 'round',
    lineJoin: 'round'
  };
}

function pickerSectionStyle(
  section: AdminCoverageSection,
  selected: boolean,
  match: boolean,
  zoom: number,
  collapseUnavailableStatuses: boolean
): Record<string, unknown> {
  const color = selected
    ? COLORS.SELECTED
    : collapseUnavailableStatuses && section.status === 'COMPLETE'
      ? COLORS.ASSIGNED
      : COLORS[section.status];
  const band = mapZoomBand(zoom);
  const weight = selected
    ? zoomInterpolate(zoom, 3, 10.5)
    : zoomInterpolate(zoom, 1, 7.25);
  return {
    color,
    weight,
    opacity: band === 'FAR'
      ? (selected ? 0.92 : 0)
      : band === 'MEDIUM'
        ? (match ? (selected ? 1 : 0.58) : (selected ? 0.74 : 0.035))
        : (match ? (selected ? 1 : 0.86) : (selected ? 0.72 : 0.08)),
    lineCap: 'round',
    lineJoin: 'round'
  };
}

function pickerHitStyle(zoom: number, match: boolean): Record<string, unknown> {
  const band = mapZoomBand(zoom);
  return {
    color: '#000000',
    weight: band === 'FAR' ? 0 : zoomInterpolate(zoom, 14, 30),
    opacity: match && band !== 'FAR' ? 0.001 : 0,
    lineCap: 'round',
    lineJoin: 'round'
  };
}

type NeighborhoodSummaryMode = 'ALL' | 'AVAILABLE';

type NeighborhoodSummary = {
  name: string;
  bounds: any;
  total: number;
  available: number;
  assigned: number;
  complete: number;
};

function populateNeighborhoodSummaryLayer(
  map: any,
  layerGroup: any,
  sections: AdminCoverageSection[],
  layerById: Map<string, any>,
  mode: NeighborhoodSummaryMode
): void {
  const summaries = new Map<string, NeighborhoodSummary>();
  for (const section of sections) {
    const layer = layerById.get(section.id);
    const layerBounds = layer?.getBounds?.();
    if (!layerBounds?.isValid?.()) continue;
    const current = summaries.get(section.neighborhoodName) ?? {
      name: section.neighborhoodName,
      bounds: L.latLngBounds([]),
      total: 0,
      available: 0,
      assigned: 0,
      complete: 0
    };
    current.bounds.extend(layerBounds);
    current.total += 1;
    if (section.status === 'UNASSIGNED') current.available += 1;
    else if (section.status === 'COMPLETE') current.complete += 1;
    else current.assigned += 1;
    summaries.set(section.neighborhoodName, current);
  }

  const renderSummaries = (): void => {
    layerGroup.clearLayers();
    const band = mapZoomBand(mapZoom(map));
    const narrowViewport = window.matchMedia('(max-width: 650px)').matches;
    const visible = Array.from(summaries.values()).map((summary) => ({
      summary,
      point: map.latLngToLayerPoint(summary.bounds.getCenter()),
      labelWidth: estimateNeighborhoodLabelWidth(summary.name, band, narrowViewport)
    }));
    const clusters: typeof visible[] = [];
    for (const entry of visible) {
      const cluster = clusters.find((items) => items.some((item) => neighborhoodLabelsOverlap(item, entry, band)));
      if (cluster) cluster.push(entry);
      else clusters.push([entry]);
    }

    for (const cluster of clusters) {
      if (cluster.length === 1) {
        addNeighborhoodSummaryMarker(map, layerGroup, cluster[0]!.summary, mode, band);
        continue;
      }
      const clusterBounds = L.latLngBounds([]);
      for (const item of cluster) clusterBounds.extend(item.summary.bounds);
      const sections = cluster.reduce((total, item) =>
        total + (mode === 'AVAILABLE' ? item.summary.available : item.summary.total), 0);
      const center = clusterBounds.getCenter();
      const names = cluster.map((item) => item.summary.name);
      const detailHtml = band === 'FAR'
        ? `<strong>${cluster.length.toLocaleString()}</strong><span>neighborhoods</span>`
        : `${names.slice(0, 2).map((name) => `<span>${escapeHtml(name)}</span>`).join('')}${names.length > 2 ? `<small>+${names.length - 2} more</small>` : ''}`;
      const marker = L.marker(center, {
        keyboard: true,
        riseOnHover: true,
        icon: L.divIcon({
          className: 'coverage-neighborhood-summary-icon',
          html: `<div class="coverage-neighborhood-cluster${band === 'FAR' ? '' : ' coverage-neighborhood-cluster--named'}">${detailHtml}</div>`,
          iconSize: [0, 0],
          iconAnchor: [0, 0]
        })
      });
      marker.bindTooltip(
        `${names.map(escapeHtml).join('<br>')}<br>${sections.toLocaleString()} ${mode === 'AVAILABLE' ? 'open ' : ''}sections<br><em>Zoom in to separate labels</em>`,
        { direction: 'top', className: 'coverage-section-tooltip' }
      );
      marker.on('click', () => map.fitBounds(clusterBounds.pad(0.12), { maxZoom: 20 }));
      marker.addTo(layerGroup);
    }
  };

  map.on('zoomend', renderSummaries);
  // whenReady is normally immediate because createMap supplies an initial view,
  // but it also protects this label projection if the map lifecycle changes later.
  map.whenReady(() => renderSummaries());
}

function addNeighborhoodSummaryMarker(
  map: any,
  layerGroup: any,
  summary: NeighborhoodSummary,
  mode: NeighborhoodSummaryMode,
  band: ReturnType<typeof mapZoomBand>
): void {
  const count = mode === 'AVAILABLE' ? summary.available : summary.total;
  const countLabel = `${count.toLocaleString()} ${mode === 'AVAILABLE' ? 'open' : count === 1 ? 'section' : 'sections'}`;
  const countHtml = band === 'CLOSE' ? '' : `<span>${escapeHtml(countLabel)}</span>`;
  const marker = L.marker(summary.bounds.getCenter(), {
    keyboard: true,
    riseOnHover: true,
    icon: L.divIcon({
      className: 'coverage-neighborhood-summary-icon',
      html: `<div class="coverage-neighborhood-summary coverage-neighborhood-summary--${band.toLowerCase()}"><strong>${escapeHtml(summary.name)}</strong>${countHtml}</div>`,
      iconSize: [0, 0],
      iconAnchor: [0, 0]
    })
  });
  marker.bindTooltip(
    `<strong>${escapeHtml(summary.name)}</strong><br>${summary.available.toLocaleString()} not assigned · ${summary.assigned.toLocaleString()} assigned · ${summary.complete.toLocaleString()} completed<br><em>Click to zoom in</em>`,
    { direction: 'top', className: 'coverage-section-tooltip' }
  );
  marker.on('click', () => map.fitBounds(summary.bounds.pad(0.18), { maxZoom: 20 }));
  marker.addTo(layerGroup);
}

function estimateNeighborhoodLabelWidth(
  name: string,
  band: ReturnType<typeof mapZoomBand>,
  narrowViewport: boolean
): number {
  if (band === 'FAR') return narrowViewport ? 168 : 210;
  const approximateTextWidth = name.length * (band === 'CLOSE' ? 6.5 : 7) + 28;
  return Math.min(narrowViewport ? 168 : 210, Math.max(100, approximateTextWidth));
}

function neighborhoodLabelsOverlap(
  first: { point: { x: number; y: number }; labelWidth: number },
  second: { point: { x: number; y: number }; labelWidth: number },
  band: ReturnType<typeof mapZoomBand>
): boolean {
  if (band === 'FAR') {
    return Math.abs(first.point.x - second.point.x) < 205 && Math.abs(first.point.y - second.point.y) < 74;
  }
  const horizontalGap = band === 'CLOSE' ? 10 : 16;
  const verticalGap = band === 'CLOSE' ? 8 : 12;
  const labelHeight = band === 'CLOSE' ? 30 : 44;
  return Math.abs(first.point.x - second.point.x) < (first.labelWidth + second.labelWidth) / 2 + horizontalGap &&
    Math.abs(first.point.y - second.point.y) < labelHeight + verticalGap;
}

function setSummaryLayerVisible(map: any, layerGroup: any, visible: boolean): void {
  if (visible && !map.hasLayer(layerGroup)) layerGroup.addTo(map);
  if (!visible && map.hasLayer(layerGroup)) map.removeLayer(layerGroup);
}

function setLayerInteractive(layer: any, enabled: boolean): void {
  const apply = (child: any): void => {
    if (child?.options) child.options.interactive = enabled;
    const path = child?._path as SVGElement | undefined;
    if (path) path.style.pointerEvents = enabled ? 'auto' : 'none';
  };
  if (typeof layer?.eachLayer === 'function') layer.eachLayer(apply);
  else apply(layer);
}

function sectionTooltip(section: AdminCoverageSection): string {
  const details = [
    `<strong>${escapeHtml(section.neighborhoodName)} · Section ${section.sectionNumber}</strong>`,
    `${escapeHtml(section.reservedForGroup && !section.assignmentId ? 'Reserved for group' : statusLabel(section.status))} · ${section.doors.toLocaleString()} doors`,
    section.assignedTo ? `Assigned to ${escapeHtml(section.assignedTo.displayName)}` : '',
    section.groupName ? `Group: ${escapeHtml(section.groupName)}` : '',
    section.assignedAt ? `Assigned ${escapeHtml(formatDate(section.assignedAt))}` : '',
    section.teamMembers.length ? `Did it: ${escapeHtml(section.teamMembers.map((member) => member.name).join(', '))}` : '',
    section.completedAt ? `Completed ${escapeHtml(formatDate(section.completedAt))}` : section.startedAt ? `Started ${escapeHtml(formatDate(section.startedAt))}` : '',
    section.completionComment ? `Comment: ${escapeHtml(section.completionComment)}` : ''
  ].filter(Boolean);
  return details.join('<br>');
}

function sectionPopup(section: AdminCoverageSection, canReturn: boolean): string {
  const rows = [
    `<div class="coverage-popup-title">${escapeHtml(section.neighborhoodName)} · Section ${section.sectionNumber}</div>`,
    `<div><strong>Status:</strong> ${escapeHtml(section.reservedForGroup && !section.assignmentId ? 'Reserved for group' : statusLabel(section.status))}</div>`,
    `<div><strong>Doors:</strong> ${section.doors.toLocaleString()}</div>`,
    section.streetNames.length ? `<div><strong>Streets:</strong> ${escapeHtml(section.streetNames.join(', '))}</div>` : '',
    section.assignedTo ? `<div><strong>Assigned to:</strong> ${escapeHtml(section.assignedTo.displayName)}</div>` : '',
    section.groupName ? `<div><strong>Small group:</strong> ${escapeHtml(section.groupName)}</div>` : '',
    section.assignedBy ? `<div><strong>Assigned by:</strong> ${escapeHtml(section.assignedBy.displayName)}</div>` : '',
    section.assignedAt ? `<div><strong>Assigned:</strong> ${escapeHtml(formatDate(section.assignedAt))}</div>` : '',
    section.teamMembers.length ? `<div><strong>Completed by:</strong> ${escapeHtml(section.teamMembers.map((member) => member.name).join(', '))}</div>` : '',
    section.startedAt ? `<div><strong>Started:</strong> ${escapeHtml(formatDate(section.startedAt))}</div>` : '',
    section.completedAt ? `<div><strong>Completed:</strong> ${escapeHtml(formatDate(section.completedAt))}</div>` : '',
    section.completionComment ? `<div class="coverage-popup-comment"><strong>Volunteer comment:</strong><p>${escapeHtml(section.completionComment)}</p></div>` : '',
    canReturn ? '<button type="button" class="coverage-return-button" data-return-section>Take back section</button>' : ''
  ].filter(Boolean);
  return `<div class="coverage-popup">${rows.join('')}</div>`;
}

function statusLabel(status: AdminCoverageSection['status']): string {
  if (status === 'COMPLETE') return 'Completed';
  if (status === 'ASSIGNED') return 'Assigned';
  return 'Not assigned';
}

function conditionColor(status: NonNullable<AdminCoverageSection['conditionPoints']>[number]['conditionStatus']): string {
  return COLORS[status];
}

function conditionLabel(status: NonNullable<AdminCoverageSection['conditionPoints']>[number]['conditionStatus']): string {
  if (status === 'EMPTY_LOT') return 'Empty lot';
  if (status === 'UNDER_CONSTRUCTION') return 'Under construction';
  return 'No Soliciting';
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function normalize(value: string): string {
  return value.trim().toLocaleLowerCase().replace(/\s+/g, ' ');
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[character] ?? character));
}
